import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnPinningState,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import * as React from "react";

import { Box } from "../../components/ui/box";
import {
  BOX_PADDING_LEFT,
  BOX_PADDING_RIGHT,
  BOX_PADDING_X,
  type SpacingToken,
} from "../../components/ui/box-classes";
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from "../../components/ui/context-menu";
import { Icon } from "../../components/ui/icon";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Text } from "../../components/ui/text";
import { useIsMobile } from "../../hooks/use-media-query";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { useWindowedPages } from "../../hooks/use-windowed-pages";
import {
  SURFACE_BG,
  SURFACE_VEIL,
  type SurfaceToken,
} from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";

import { DataTableCell, DataTableCellContent } from "./data-table-cell";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableEmpty } from "./data-table-empty";
import { DataTableGroupHeader } from "./data-table-group";
import {
  DataTableGroupNav,
  type DataTableGroupNavItem,
  type DataTableGroupNavTarget,
} from "./data-table-group-nav";
import { useDataTable } from "./data-table-hook";
import {
  DataTableOverlayProvider,
  useContextMenuClaim,
} from "./data-table-overlay";
import { BulkActionsBar } from "../bulk-actions-bar/bulk-actions-bar";
import {
  DataTableAddColumnProvider,
  DataTableSurfaceProvider,
  useDataTableSurface,
  DataTableDensityProvider,
  DataTableGroupRowsProvider,
  DataTableHeaderDragProvider,
  DataTableShiftSelectProvider,
  DataTableSelectAllProvider,
  useDataTableSelectAll,
} from "./data-table-context";
import {
  DataTableResizeHandle,
  useColumnResize,
  type ColumnResizeController,
} from "./data-table-resize";
import { useColumnReorder } from "./data-table-reorder";
import { useDataTableVirtualizer } from "./data-table-virtualizer";
import { createActionsColumn } from "./data-table-actions-column";
import { createSelectionColumn } from "./data-table-selection-column";
import type {
  DataTableColumnDef,
  DataTableColumnMeta,
  DataTableDensity,
  DataTableGroup,
  DataTableProps,
} from "./data-table-types";
import type { SelectAllGroup } from "../bulk-actions-bar/bulk-actions-bar-types";
import {
  ALL_GROUP_KEY,
  DENSITY_CONFIG,
  STRUCTURAL_COLUMN_IDS,
  TABLE_TOTAL_WIDTH_VAR,
  columnLeftVar,
  columnRightVar,
  columnWidthVar,
  getColumnDefId,
  getColumnLabel,
  getColumnMeta,
  getTruncationClass,
  mergeColumnOrder,
  resolveColumnBaseWidth,
  resolveColumnWidths,
  computeColumnMove,
  type ColumnMoveTarget,
} from "./data-table-utils";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PINNED_COLUMN_IDS = ["select"];
const EMPTY_PINNED_COLUMN_IDS: string[] = [];
const RIGHT_PINNED_ACTIONS = ["__actions__"];
const EMPTY_COLUMN_VISIBILITY: VisibilityState = {};
const EMPTY_COLUMN_SIZING: Record<string, number> = {};

/** Shared min-width so rows/headers span the full resolved table width. */
const TABLE_MIN_WIDTH_STYLE: React.CSSProperties = {
  // Never narrower than the container: shrinking columns below the viewport
  // width must not pull row borders/backgrounds short of the right edge.
  minWidth: `max(100%, var(${TABLE_TOTAL_WIDTH_VAR}))`,
};

// Hoist row-model factories to module scope so TanStack Table's internal
// memoization sees stable references.  Creating these inline inside each
// group component caused the full row-model pipeline to rerun on every
// scroll-driven re-render.
const CORE_ROW_MODEL = getCoreRowModel();
const SORTED_ROW_MODEL = getSortedRowModel();
let lastMeasuredTableContainerWidth = 0;

// Fallback group header height before layout measurement runs.
const DEFAULT_GROUP_HEADER_HEIGHT = 36;
// Stable no-op so `useWindowedPages` always gets a fetchPage identity for groups
// without one (the `enabled` flag gates actual fetching).
const NOOP_FETCH_PAGE = () => {};
const HEADER_TEXT_CLASS = "text-xs font-medium text-muted-foreground";

// ---------------------------------------------------------------------------
// Surface recipes
//
// The table names ONE surface (the `surface` prop, `background` by default) and
// every part reads it from context, so the header, the pinned cells and the
// row washes cannot drift apart. The washes are the foreground color at low alpha, so a hovered
// row on a card and the same row on a dialog both read as the same wash rather
// than as a hole.
//
// The row-state classes repeat the base TableRow stock modifier chains
// (`not-in-data-[variant=card]:hover:` and the `dark:` twins) EXACTLY —
// The `not-in-data-[variant=card]` chain is part of each class: tailwind-merge
// only drops a stock class when the replacement's modifier chain is an
// identical string, and a bare `hover:` override would otherwise lose to the
// stock `dark:` variants. So these are written out whole rather than composed.
// ---------------------------------------------------------------------------

// Hover and selected come from TableRow. This is the one rung it has no reason
// to know about — a selected row that is also hovered.
const ROW_SELECTED_HOVER_CLASS =
  "not-in-data-[variant=card]:data-selected:hover:[--row-wash:var(--wash-selected-hover)]";
/** Header rows keep the stock chains but paint nothing — thead owns the bg. */
const ROW_STATES_OFF_CLASS =
  "not-in-data-[variant=card]:hover:[--row-wash:transparent] not-in-data-[variant=card]:data-selected:[--row-wash:transparent]";

/**
 * A pinned cell paints its own opaque fill — the scrolling columns pass BEHIND
 * it — and then replays the row's wash as a layer on top of that fill, so the
 * pinned block and the scrolling block land on the same composited color. The
 * fill is `SURFACE_BG[surface]` in `background-color`; this is the layer.
 */
const PINNED_CELL_WASH_CLASS =
  "bg-linear-to-b from-(--row-wash) to-(--row-wash)";

/**
 * The table's fill as a VALUE, declared once on the scroll container and
 * inherited by every row and cell. Nothing downstream names a surface: a sticky
 * cell and its fade both paint `--table-fill` under `--row-wash`, so all three
 * layers change together with the state and the `surface` prop.
 */
const SURFACE_FILL_VAR: Record<SurfaceToken, string> = {
  sunken: "[--table-fill:var(--sunken)]",
  background: "[--table-fill:var(--background)]",
  card: "[--table-fill:var(--card)]",
  elevated: "[--table-fill:var(--elevated)]",
  popover: "[--table-fill:var(--popover)]",
  sidebar: "[--table-fill:var(--sidebar)]",
};

/**
 * Fade off the trailing edge of the left-pinned block, so the columns scrolling
 * behind it read as passing under it rather than being chopped at a hard line.
 * A box-shadow, not an overlay: the pinned cell clips its own content for
 * truncation, and a shadow is painted outside that clip. Two layers in the same
 * order as the cell's own — wash over fill — so the fade is whatever the row is
 * painted, hovered, selected or neither.
 */
const PINNED_FADE_END_CLASS =
  "shadow-[10px_0_10px_-5px_var(--row-wash),10px_0_10px_-5px_var(--table-fill)]";

/**
 * The same fade mirrored for the leading edge of the RIGHT-pinned block (the
 * actions column), so columns scrolling toward it read as passing under it —
 * the cue that more columns exist to the right.
 */
const PINNED_FADE_START_CLASS =
  "shadow-[-10px_0_10px_-5px_var(--row-wash),-10px_0_10px_-5px_var(--table-fill)]";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAlignmentClass(
  meta?: DataTableColumnMeta,
  isSelectionColumn?: boolean,
) {
  if (isSelectionColumn) return "justify-center";
  if (meta?.isNumeric) return "justify-end";
  return "justify-start";
}

// Stable empty references to avoid re-creating tables on every render
const EMPTY_SORTING: SortingState = [];
const EMPTY_ROW_SELECTION: RowSelectionState = {};

// ---------------------------------------------------------------------------
// DataTableGroupContent — per-group sub-table with its own virtualizer
// ---------------------------------------------------------------------------

type DataTableGroupContentProps<TData> = Omittable<{
  group: DataTableGroup<TData>;
  groupKey: string;
  columns: DataTableColumnDef<TData>[];
  isExpanded: boolean;
  density: DataTableDensity;
  getRowId?: (row: TData, index: number) => string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  canSelectRow?: (row: TData) => boolean;
  /** Shared TanStack state — same references for every group. */
  columnPinning: ColumnPinningState;
  tableColumnOrder: string[];
  tableColumnVisibility: VisibilityState;
  /** Per-column-id styles (CSS-var widths + sticky offsets), built once in the outer table. */
  headerCellStyles: Map<string, React.CSSProperties>;
  bodyCellStyles: Map<string, React.CSSProperties>;
  /** Column resize drag controller (stable identity). */
  resizeController: ColumnResizeController;
  activeResizeColumnId: string | null;
  /** Column id currently being drag-reordered (dims its header). */
  draggingColumnId: string | null;
  canResizeColumns: boolean;
  reorderableIdSet: ReadonlySet<string>;
  /** Reorderable column ids in on-screen order — drives the header context
   *  menu's move targets and edge-disabled states. */
  reorderableIds: readonly string[];
  /** Commits a discrete column move in drag-drop terms (insert before
   *  `beforeId`; null = end of the reorderable span). */
  onColumnMove: (columnId: string, beforeId: string | null) => void;
  containerWidth: number;
  loading?: boolean;
  emptyState?: React.ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  rowClickable?: (row: Row<TData>) => boolean;
  extraData?: unknown;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  // Group header rendering
  groupTitle?: (groupKey: string) => React.ReactNode;
  groupActions?: (
    context: import("./data-table-types").DataTableGroupActionsContext<TData>,
  ) => React.ReactNode;
  onToggle: (key: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  allCollapsed: boolean;
  stickyHeader: boolean;
  scrollMode: "internal" | "external" | "fit";
  /** Suppress the column-header row. */
  hideHeader?: boolean;
  isUngrouped: boolean;
  hideGroupCount?: boolean;
  paddingX?: SpacingToken;
  paddingXAlign?: "edge" | "content";
  renderRowActions?: (row: TData) => React.ReactNode;
  // Group navigator
  groupNavEnabled: boolean;
  groupIndex: number;
  groupTotal: number;
  navItems: DataTableGroupNavItem[];
  navigateToGroup: (target: DataTableGroupNavTarget) => void;
  /** Registers this group's sticky header DOM node for scroll-to navigation. */
  registerGroupHeaderNode: (node: HTMLElement | null) => void;
}>;

/**
 * Placeholder body for groups that haven't loaded data yet.
 * Uses IntersectionObserver to call onVisible when scrolled into the viewport.
 */
function PlaceholderBody({
  height,
  onVisible,
  ref,
}: Omittable<{
  height: number;
  onVisible?: () => void;
  ref?: React.Ref<HTMLDivElement>;
}>) {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const firedRef = React.useRef(false);

  React.useImperativeHandle(ref, () => innerRef.current!);

  React.useEffect(() => {
    if (!onVisible || firedRef.current) return;
    // Don't observe zero-height elements — they're always "intersecting"
    if (height <= 0) return;
    const el = innerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onVisible();
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onVisible]);

  // The scroll container is a flex column, so a plain block would shrink to
  // nothing under it and the group would reserve no scroll space at all —
  // the sentinel would then sit at the very bottom and never intersect.
  return <Box ref={innerRef} shrink={false} style={{ height }} />;
}

function DataTableGroupContentInner<TData>({
  group,
  groupKey,
  columns,
  isExpanded,
  density,
  getRowId,
  sorting,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  canSelectRow,
  columnPinning,
  tableColumnOrder,
  tableColumnVisibility,
  headerCellStyles,
  bodyCellStyles,
  resizeController,
  activeResizeColumnId,
  draggingColumnId,
  canResizeColumns,
  reorderableIdSet,
  reorderableIds,
  onColumnMove,
  containerWidth,
  loading: tableLoading,
  emptyState,
  onRowClick,
  rowClickable,
  extraData,
  scrollContainerRef,
  groupTitle,
  groupActions,
  onToggle,
  collapseAll,
  expandAll,
  allCollapsed,
  stickyHeader,
  scrollMode,
  hideHeader,
  isUngrouped,
  hideGroupCount,
  paddingX,
  paddingXAlign,
  renderRowActions,
  groupNavEnabled,
  groupIndex,
  groupTotal,
  navItems,
  navigateToGroup,
  registerGroupHeaderNode,
}: DataTableGroupContentProps<TData>) {
  const surface = useDataTableSurface();
  const densityConfig = DENSITY_CONFIG[density];
  const rowHeight = densityConfig.rowHeight;
  // Only show loading state when there are truly no rows yet (initial load).
  // Once rows exist, they must NEVER be hidden by a loading indicator.
  const hasData = group.data.length > 0;
  const groupLoading = (tableLoading || group.loading) && !hasData;

  // -------------------------------------------------------------------------
  // Sticky header config
  // -------------------------------------------------------------------------

  const stickyEnabled = stickyHeader;
  const useInternalScroll = scrollMode === "internal";
  const [groupHeaderHeight, setGroupHeaderHeight] = React.useState(
    DEFAULT_GROUP_HEADER_HEIGHT,
  );

  // -------------------------------------------------------------------------
  // Per-group TanStack Table
  // -------------------------------------------------------------------------

  const table = useDataTable({
    data: group.data,
    columns,
    getCoreRowModel: CORE_ROW_MODEL,
    getSortedRowModel: SORTED_ROW_MODEL,
    manualSorting: true,
    getRowId,
    enableRowSelection: onRowSelectionChange
      ? canSelectRow
        ? (row) => canSelectRow(row.original)
        : true
      : false,
    state: {
      sorting: sorting ?? EMPTY_SORTING,
      rowSelection: rowSelection ?? EMPTY_ROW_SELECTION,
      columnPinning,
      columnOrder: tableColumnOrder,
      columnVisibility: tableColumnVisibility,
    },
    onSortingChange,
    onRowSelectionChange,
  });

  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();

  // Track reactive state so renders refresh when these change
  const _rowSelection = table.getState().rowSelection;
  const _sorting = table.getState().sorting;
  void _rowSelection;
  void _sorting;
  void extraData;

  // -------------------------------------------------------------------------
  // Visual column order
  //
  // Header and body cells render in TanStack's pinning-partitioned order
  // (left → center → right), which diverges from getVisibleLeafColumns()
  // (pure columnOrder) once right-pinning and reorder exist. Every
  // per-column lookup is keyed by column id — never array index — and all
  // width/offset values live in the CSS variables the outer table maintains.
  // -------------------------------------------------------------------------

  const visualColumns = React.useMemo(
    () => headerGroups[0]?.headers.map((h) => h.column) ?? [],
    [headerGroups],
  );
  const visualColumnIds = visualColumns.map((c) => c.id).join(",");

  // -------------------------------------------------------------------------
  // Virtualizer (shared scroll container, per-group scrollMargin)
  // -------------------------------------------------------------------------

  // A Base UI menu root per row and per header cell is the single largest
  // block of scroll-frame work, torn down and rebuilt on every row recycle.
  // Rows carry only a pointerdown closure instead, handing the table's shared
  // menu host a thunk for their own items.
  const claimContextMenu = useContextMenuClaim();

  const groupBodyRef = React.useRef<HTMLDivElement | null>(null);
  const rowsContainerRef = React.useRef<HTMLTableSectionElement | null>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  // Measure offset from scroll container to rows container for the
  // virtualizer's scrollMargin.  Deferred to rAF so getBoundingClientRect()
  // runs after browser layout is computed (nearly free) rather than forcing
  // a synchronous reflow.  The cleanup cancels the previous frame's rAF so
  // only the latest render's measurement fires.  The computed offset is
  // stable during scroll (scrollTop and element position change equally),
  // so setScrollMargin is a no-op on scroll frames and won't loop.
  React.useEffect(() => {
    if (!isExpanded) return;
    const id = requestAnimationFrame(() => {
      const scrollEl = scrollContainerRef.current;
      const rowsEl = rowsContainerRef.current;
      if (!scrollEl || !rowsEl) return;
      const scrollRect = scrollEl.getBoundingClientRect();
      const rowsRect = rowsEl.getBoundingClientRect();
      const offset = Math.round(
        rowsRect.top - scrollRect.top + scrollEl.scrollTop,
      );
      setScrollMargin((prev) => (prev === offset ? prev : offset));
    });
    return () => cancelAnimationFrame(id);
    // Re-measure when expansion state or row count changes (layout shifts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, rows.length]);

  // When a numeric totalCount is provided and exceeds loaded rows, size
  // the virtualizer to the full server total so the scrollbar reflects
  // the true dataset size.  Unloaded indices render skeleton placeholders.
  const virtualCount =
    typeof group.totalCount === "number" && group.totalCount > rows.length
      ? group.totalCount
      : rows.length;

  const virtualizer = useDataTableVirtualizer({
    count: virtualCount,
    rowHeight,
    scrollContainerRef,
    scrollMargin,
    enabled: useInternalScroll && isExpanded && virtualCount > 0,
  });

  // -------------------------------------------------------------------------
  // Offset-based page fetching
  // -------------------------------------------------------------------------

  // Ref for row selection so the scroll handler always sees latest state
  // without adding it as an effect dependency.
  const rowSelectionRef = React.useRef(rowSelection);
  rowSelectionRef.current = rowSelection;

  // Windowed paging: virtualizer sized to the full server total, only a window of
  // pages in memory. `useWindowedPages` owns the page cache, fetch scheduling,
  // eviction, and reset-on-variablesKey; `getWindowRow(index)` resolves the row
  // or undefined (→ skeleton). Pages holding selected rows are kept via `keepPage`.
  const PAGE_SIZE = group.pageSize ?? 50;
  const dataOffset = group.dataOffset ?? 0;

  const { getItem: getWindowRow, cacheVersion } = useWindowedPages<Row<TData>>({
    total: virtualCount,
    pageSize: PAGE_SIZE,
    pageItems: rows,
    dataOffset,
    variablesKey: group.variablesKey ?? "",
    fetchPage: group.fetchPage ?? NOOP_FETCH_PAGE,
    virtualizer,
    scrollElementRef: scrollContainerRef,
    enabled:
      useInternalScroll &&
      isExpanded &&
      !groupLoading &&
      !group.placeholder &&
      !!group.fetchPage &&
      virtualCount > 0,
    keepPage: (_pageStart, pageRows) => {
      const sel = rowSelectionRef.current;
      return sel ? pageRows.some((r) => sel[r.id]) : false;
    },
  });

  // -------------------------------------------------------------------------
  // Render: column headers (single pass — pinned columns get sticky style)
  // -------------------------------------------------------------------------

  const stickyColumnHeaderTop = stickyEnabled
    ? isUngrouped
      ? 0
      : groupHeaderHeight
    : undefined;

  const renderHeader = React.useCallback(() => {
    return (
      <TableHeader
        // `[&_tr]:border-0` tw-merges away the stock `[&_tr]:border-b` (same
        // modifier chain) — the thead draws the header's bottom border itself,
        // and the arbitrary-variant selector would out-specify a plain
        // `border-0` on the row.
        className={cn(
          "block border-b border-border/80 [&_tr]:border-0",
          SURFACE_BG[surface],
        )}
        style={
          stickyColumnHeaderTop !== undefined
            ? {
                position: "sticky" as const,
                top: stickyColumnHeaderTop,
                zIndex: 20,
              }
            : undefined
        }
      >
        {headerGroups.map((headerGroup, groupIndex) => {
          return (
            <TableRow
              key={headerGroup.id}
              role="row"
              className={cn(
                "flex w-full border-0",
                ROW_STATES_OFF_CLASS,
                // Headers are drag/sort surfaces — text selection mid-gesture
                // reads as breakage.
                "select-none",
                "group/header",
              )}
              style={{
                height: densityConfig.rowHeight,
                ...TABLE_MIN_WIDTH_STYLE,
                // Divider between stacked header groups (rare: only column
                // grouping produces >1). Inline because the thead-level
                // `[&_tr]:border-0` above out-specifies row border classes.
                ...(groupIndex < headerGroups.length - 1
                  ? {
                      borderBottom:
                        "1px solid color-mix(in oklab, var(--border) 60%, transparent)",
                    }
                  : null),
              }}
            >
              {headerGroup.headers.map((header, headerIndex) => {
                const columnId = header.column.id;
                const meta = getColumnMeta(header.column);
                const isSelectionColumn = columnId === "select";
                const isPinned = !!header.column.getIsPinned();
                const isFirst = headerIndex === 0;
                const isLast = headerIndex === headerGroup.headers.length - 1;
                const canResize =
                  canResizeColumns &&
                  !STRUCTURAL_COLUMN_IDS.has(columnId) &&
                  header.column.columnDef.enableResizing !== false;
                const cell = (
                  <TableHead
                    key={header.id}
                    role="columnheader"
                    className={cn(
                      // `relative` anchors the absolutely-positioned resize
                      // handle.
                      "relative flex h-auto items-center whitespace-normal p-0",
                      HEADER_TEXT_CLASS,
                      "group/hcell",
                      getAlignmentClass(meta, isSelectionColumn),
                      getTruncationClass(meta),
                      isPinned && "bg-(--table-fill)",
                      isPinned &&
                        header.column.getIsPinned() === "left" &&
                        headerGroup.headers[headerIndex + 1]?.column.getIsPinned() !==
                          "left" &&
                        PINNED_FADE_END_CLASS,
                      isPinned &&
                        header.column.getIsPinned() === "right" &&
                        headerGroup.headers[headerIndex - 1]?.column.getIsPinned() !==
                          "right" &&
                        PINNED_FADE_START_CLASS,
                      draggingColumnId === columnId && "opacity-50",
                      // paddingXAlign picks what paddingX measures: 'edge'
                      // (default) is the raw inset outside the cell; 'content'
                      // density-compensates content columns so their CONTENT
                      // lands exactly paddingX from the table edge (structural
                      // select/actions columns take it raw either way).
                      paddingX &&
                        isFirst &&
                        (paddingXAlign !== "content" ||
                        STRUCTURAL_COLUMN_IDS.has(columnId)
                          ? BOX_PADDING_LEFT[paddingX]
                          : densityConfig.edgeGutterLeft[paddingX]),
                      paddingX &&
                        isLast &&
                        (paddingXAlign !== "content" ||
                        STRUCTURAL_COLUMN_IDS.has(columnId)
                          ? BOX_PADDING_RIGHT[paddingX]
                          : densityConfig.edgeGutterRight[paddingX]),
                    )}
                    style={headerCellStyles.get(columnId)}
                    data-dthcell="true"
                    {...(reorderableIdSet.has(columnId)
                      ? { "data-dtreorderid": columnId }
                      : null)}
                  >
                    {header.isPlaceholder
                      ? null
                      : (() => {
                          const content = flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          );
                          if (STRUCTURAL_COLUMN_IDS.has(header.column.id))
                            return content;
                          // Component-typed headers own their chrome — the
                          // documented idiom renders DataTableColumnHeader
                          // itself. flexRender types the element by the
                          // consumer's component (never DataTableColumnHeader
                          // directly), so wrapping again would nest two sort
                          // buttons and a click would toggle the sort twice.
                          // Only plain content (string headers) gets the
                          // automatic sort chrome.
                          if (
                            React.isValidElement(content) &&
                            typeof content.type !== "string"
                          ) {
                            return content;
                          }
                          return (
                            <DataTableColumnHeader
                              column={header.column}
                              title={content}
                            />
                          );
                        })()}
                    {/* Unmounted during a reorder drag — the boundary hover
                        lines and grabbable strip are resize affordances and
                        read as noise while a column is being moved. */}
                    {canResize && draggingColumnId === null ? (
                      <DataTableResizeHandle
                        columnId={columnId}
                        controller={resizeController}
                        active={activeResizeColumnId === columnId}
                      />
                    ) : null}
                  </TableHead>
                );
                // Structural and pinned columns are not reorderable, so they
                // get no header context menu at all — gating on the same set
                // the drag-reorder uses keeps the two affordances in lockstep.
                if (!reorderableIdSet.has(columnId)) return cell;
                const reorderIndex = reorderableIds.indexOf(columnId);
                const isFirstReorderable = reorderIndex <= 0;
                const isLastReorderable =
                  reorderIndex >= reorderableIds.length - 1;
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const move = (target: ColumnMoveTarget) => {
                  const result = computeColumnMove(
                    columnId,
                    reorderableIds,
                    target,
                  );
                  if (result) onColumnMove(columnId, result.beforeId);
                };
                const setSort = (dir: "asc" | "desc") => {
                  // Selecting the active direction clears the sort, matching
                  // the header press cycle's final step.
                  if (sorted === dir) header.column.clearSorting();
                  else header.column.toggleSorting(dir === "desc");
                };
                // Same shared-host treatment as rows: the header cell carries a
                // pointerdown claim, not a menu root of its own. One table has
                // a header row per group — otherwise N x columns more roots.
                const renderColumnMenu = () => (
                  <>
                    {canSort ? (
                        <>
                          <ContextMenuItem onClick={() => setSort("asc")}>
                            <Icon
                              name={sorted === "asc" ? "check" : "arrow-up"}
                              size="lg"
                              color="muted"
                            />
                            <Text size="sm">Sort ascending</Text>
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => setSort("desc")}>
                            <Icon
                              name={sorted === "desc" ? "check" : "arrow-down"}
                              size="lg"
                              color="muted"
                            />
                            <Text size="sm">Sort descending</Text>
                          </ContextMenuItem>
                          <ContextMenuSeparator />
                        </>
                      ) : null}
                      <ContextMenuItem
                        disabled={isFirstReorderable}
                        onClick={() => move("left")}
                      >
                        <Icon name="arrow-left" size="lg" color="muted" />
                        <Text size="sm">Move Left</Text>
                      </ContextMenuItem>
                      <ContextMenuItem
                        disabled={isLastReorderable}
                        onClick={() => move("right")}
                      >
                        <Icon name="arrow-right" size="lg" color="muted" />
                        <Text size="sm">Move Right</Text>
                      </ContextMenuItem>
                      <ContextMenuItem
                        disabled={isFirstReorderable}
                        onClick={() => move("start")}
                      >
                        <Icon name="chevrons-left" size="lg" color="muted" />
                        <Text size="sm">Move to Start</Text>
                      </ContextMenuItem>
                      <ContextMenuItem
                        disabled={isLastReorderable}
                        onClick={() => move("end")}
                      >
                        <Icon name="chevrons-right" size="lg" color="muted" />
                        <Text size="sm">Move to End</Text>
                      </ContextMenuItem>
                  </>
                );
                if (!claimContextMenu) return cell;
                return React.cloneElement(cell, {
                  onPointerDown: claimContextMenu(renderColumnMenu),
                });
              })}
            </TableRow>
          );
        })}
      </TableHeader>
    );
    // The header cells render through `flexRender`, which reads
    // `column.getIsSorted()` when it is CALLED — inside this memo, where the
    // rule cannot follow it. `_sorting` is that read, hoisted so the memo
    // recomputes when the sort changes; drop it and the sort arrows freeze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    headerGroups,
    densityConfig,
    headerCellStyles,
    _sorting,
    stickyColumnHeaderTop,
    paddingX,
    paddingXAlign,
    canResizeColumns,
    resizeController,
    activeResizeColumnId,
    draggingColumnId,
    reorderableIdSet,
    reorderableIds,
    onColumnMove,
    claimContextMenu,
    surface,
  ]);

  // -------------------------------------------------------------------------
  // Pre-computed class strings for the render hot-path.
  // Eliminates hundreds of cn() (clsx + twMerge) calls per scroll frame
  // by computing them once when columns/density/padding change.
  // -------------------------------------------------------------------------

  const rowClasses = React.useMemo(() => {
    // Selected paint is driven by the `data-selected` attribute, so
    // one class string covers both states — the density text size rides on
    // the row and inherits into `size={null}` cell Text.
    const base = cn(
      "group/row relative flex w-full border-0",
      densityConfig.textSize,
      SURFACE_BG[surface],
      ROW_SELECTED_HOVER_CLASS,
    );
    return {
      base,
      clickable: cn(base, "cursor-pointer"),
    };
  }, [densityConfig.textSize, surface]);

  const cellClasses = React.useMemo(() => {
    const map = new Map<string, { regular: string; last: string }>();
    visualColumns.forEach((col, cellIndex) => {
      const meta = getColumnMeta(col);
      const isPinned = !!col.getIsPinned();
      const isFirst = cellIndex === 0;
      const isLast = cellIndex === visualColumns.length - 1;
      const base = cn(
        "relative flex items-stretch whitespace-normal p-0",
        isPinned ? "z-10" : "z-0",
        getTruncationClass(meta),
        // Same edge/content rule as the header cells above.
        paddingX &&
          isFirst &&
          (paddingXAlign !== "content" || STRUCTURAL_COLUMN_IDS.has(col.id)
            ? BOX_PADDING_LEFT[paddingX]
            : densityConfig.edgeGutterLeft[paddingX]),
        paddingX &&
          isLast &&
          (paddingXAlign !== "content" || STRUCTURAL_COLUMN_IDS.has(col.id)
            ? BOX_PADDING_RIGHT[paddingX]
            : densityConfig.edgeGutterRight[paddingX]),
      );
      // The trailing edge of the left-pinned block — the last cell before the
      // scrolling columns start — and the leading edge of the right-pinned
      // block, where they end. Each gets the fade pointing at the scrolling
      // columns.
      const isPinnedEnd =
        col.getIsPinned() === "left" &&
        visualColumns[cellIndex + 1]?.getIsPinned() !== "left";
      const isPinnedStart =
        col.getIsPinned() === "right" &&
        visualColumns[cellIndex - 1]?.getIsPinned() !== "right";
      const pinned = isPinned
        ? cn(
            base,
            "bg-(--table-fill)",
            PINNED_CELL_WASH_CLASS,
            isPinnedEnd && PINNED_FADE_END_CLASS,
            isPinnedStart && PINNED_FADE_START_CLASS,
          )
        : base;
      map.set(col.id, {
        regular: cn(pinned, "border-b border-border/60"),
        last: pinned,
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualColumnIds, densityConfig, paddingX, paddingXAlign]);

  // -------------------------------------------------------------------------
  // Render: single row (single pass — pinned cells get sticky style)
  // -------------------------------------------------------------------------

  // Pre-computed row style — shared by all rows; virtual rows merge their
  // absolute positioning on top.
  const rowStyle = React.useMemo<React.CSSProperties>(
    () => ({ height: densityConfig.rowHeight, ...TABLE_MIN_WIDTH_STYLE }),
    [densityConfig.rowHeight],
  );

  const { isSelectAll, setSelectAll } = useDataTableSelectAll();

  const renderRow = React.useCallback(
    (
      row: Row<TData>,
      index: number,
      groupRowCount: number,
      virtualStyle?: React.CSSProperties,
    ) => {
      const visibleCells = row.getVisibleCells();
      const isClickable =
        !!onRowClick && (rowClickable ? rowClickable(row) : true);
      const isSelected = row.getIsSelected() || isSelectAll;
      const isLastRow = index === groupRowCount - 1;

      const rowElement = (
        <TableRow
          // Namespaced because a loaded row and an unloaded slot share ONE key
          // space. A skeleton is keyed by its virtual INDEX, and React
          // stringifies keys, so a bare `row.id` of "1" collides with slot 1 —
          // which is not a corner case but the ordinary server-paginated
          // window, where ids and indices both start counting at the top.
          // React's answer to a duplicate key is to reuse one child's DOM for
          // the other or drop it, so a skeleton keeps the row's place.
          key={`row-${row.id}`}
          role="row"
          aria-selected={isSelected}
          data-selected={isSelected ? "" : undefined}
          className={isClickable ? rowClasses.clickable : rowClasses.base}
          style={virtualStyle ? { ...rowStyle, ...virtualStyle } : rowStyle}
          onClick={
            isClickable
              ? (event: React.MouseEvent<HTMLElement>) => {
                  // React portals bubble through the REACT tree, not the DOM
                  // tree: a click inside an overlay a cell opened (a select
                  // popup, a dialog) re-fires here even though the DOM node
                  // lives under document.body. Only a click physically inside
                  // the row counts.
                  if (!event.currentTarget.contains(event.target as Node))
                    return;
                  onRowClick(row);
                }
              : undefined
          }
          onPointerDown={
            renderRowActions && claimContextMenu
              ? claimContextMenu(() => renderRowActions(row.original))
              : undefined
          }
        >
          {visibleCells.map((cell) => {
            const precomputed = cellClasses.get(cell.column.id);
            return (
              <TableCell
                key={cell.id}
                role="cell"
                className={isLastRow ? precomputed?.last : precomputed?.regular}
                style={bodyCellStyles.get(cell.column.id)}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
      );

      return rowElement;
    },
    [
      onRowClick,
      rowClickable,
      isSelectAll,
      rowStyle,
      bodyCellStyles,
      rowClasses,
      cellClasses,
      renderRowActions,
      claimContextMenu,
    ],
  );

  // -------------------------------------------------------------------------
  // Render: virtualized rows (single pass — no pane splitting)
  // -------------------------------------------------------------------------

  // Only ever rendered for a windowed slot whose row has not loaded, so the
  // virtual key and slot style are always known.
  const renderSkeletonRow = React.useCallback(
    (
      index: number,
      total: number,
      virtualStyle: React.CSSProperties,
      key: React.Key,
    ) => {
      const isLastRow = index === total - 1;
      return (
        <TableRow
          // The other half of the namespace `renderRow` opens: `slot-` can
          // never equal `row-`, so no row id can collide with a virtual index
          // however the two are numbered.
          key={`slot-${key}`}
          role="row"
          className={rowClasses.base}
          style={{ ...rowStyle, ...virtualStyle }}
        >
          {visualColumns.map((col, idx) => {
            const precomputed = cellClasses.get(col.id);
            return (
              <TableCell
                key={col.id}
                role="cell"
                className={isLastRow ? precomputed?.last : precomputed?.regular}
                style={bodyCellStyles.get(col.id)}
              >
                <DataTableCellContent>
                  <Skeleton
                    className={
                      idx === 0 ? "h-3 w-3/4 rounded" : "h-3 w-1/2 rounded"
                    }
                  />
                </DataTableCellContent>
              </TableCell>
            );
          })}
        </TableRow>
      );
    },
    [rowClasses, rowStyle, visualColumns, cellClasses, bodyCellStyles],
  );

  const renderVirtualRows = React.useCallback(
    () => {
      if (!virtualizer) {
        // Non-virtualized fallback (virtualizer disabled — external/fit)
        return rows.map((row, index) => renderRow(row, index, rows.length));
      }

      const items = virtualizer.getVirtualItems();
      const margin = virtualizer.options.scrollMargin;

      // The tbody itself is the sized positioning context (block + relative +
      // height: totalSize) — each virtual row is an absolutely positioned <tr>
      // translated to its slot, so no non-table wrapper enters the table.
      return items.map((virtualItem) => {
        const row = getWindowRow(virtualItem.index);
        const virtualStyle: React.CSSProperties = {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: virtualItem.size,
          transform: `translateY(${virtualItem.start - margin}px)`,
        };
        return row
          ? renderRow(row, virtualItem.index, virtualCount, virtualStyle)
          : renderSkeletonRow(
              virtualItem.index,
              virtualCount,
              virtualStyle,
              virtualItem.key,
            );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      virtualizer,
      rows,
      renderRow,
      renderSkeletonRow,
      virtualCount,
      getWindowRow,
      cacheVersion,
    ],
  );

  // -------------------------------------------------------------------------
  // Group header with access to per-group rows
  // -------------------------------------------------------------------------

  const selectAll = React.useCallback(() => {
    table.setRowSelection((old) => {
      const next = { ...old };
      for (const row of rows) {
        if (row.getCanSelect()) next[row.id] = true;
      }
      return next;
    });
    setSelectAll(true);
  }, [table, rows, setSelectAll]);

  const deselectAll = React.useCallback(() => {
    for (const row of rows) {
      row.toggleSelected(false);
    }
    // Clearing the rows is not enough: `isSelectAll` also paints every row
    // selected (it covers rows evicted with their page) and keeps the group in
    // the bulk bar's `selectAllGroups`. Leaving it armed left a "deselected"
    // group still washed selected, still counted, and with nothing checked —
    // the same pairing the header checkbox and the per-row cell already make.
    setSelectAll(false);
  }, [rows, setSelectAll]);

  // Only selectable rows count toward "group fully selected" — rows the
  // viewer can't act on have no checkbox and can never be selected.
  const selectableRows = rows.filter((r) => r.getCanSelect());
  const isGroupSelected =
    selectableRows.length > 0 && selectableRows.every((r) => r.getIsSelected());

  // Bind the key-accepting onToggle to this group's key for child components
  // that expect () => void.  Stable as long as onToggle + groupKey are stable.
  const handleToggle = React.useCallback(
    () => onToggle(groupKey),
    [onToggle, groupKey],
  );

  const groupCount = group.totalCount ?? group.data.length;
  // Memoized because the group header below memoizes on it: rebuilt inline,
  // this element is a new reference every render and the header's memo never
  // holds.
  const navNode = React.useMemo(
    () =>
      groupNavEnabled ? (
        <DataTableGroupNav
          index={groupIndex}
          total={groupTotal}
          items={navItems}
          currentKey={groupKey}
          onNavigate={navigateToGroup}
        />
      ) : null,
    [
      groupNavEnabled,
      groupIndex,
      groupTotal,
      navItems,
      groupKey,
      navigateToGroup,
    ],
  );
  const groupHeader = React.useMemo(
    () => (
      <DataTableGroupHeader
        isExpanded={isExpanded}
        onToggle={handleToggle}
        title={groupTitle ? groupTitle(groupKey) : group.label}
        count={hideGroupCount ? undefined : groupCount}
        nav={navNode}
        disabled={groupCount === 0}
        className={paddingX ? BOX_PADDING_X[paddingX] : undefined}
        actions={
          groupCount === 0
            ? undefined
            : groupActions?.({
                groupKey,
                rows,
                isCollapsed: !isExpanded,
                isGroupSelected,
                collapseGroup: handleToggle,
                collapseAll,
                expandAll,
                allCollapsed,
                selectAll,
                deselectAll,
              })
        }
      />
    ),
    [
      isExpanded,
      handleToggle,
      groupTitle,
      groupKey,
      group.label,
      groupCount,
      hideGroupCount,
      navNode,
      paddingX,
      groupActions,
      rows,
      collapseAll,
      expandAll,
      allCollapsed,
      selectAll,
      deselectAll,
      isGroupSelected,
    ],
  );

  // -------------------------------------------------------------------------
  // Render: group body content
  // -------------------------------------------------------------------------

  // When loading with a known total, render an empty placeholder sized to
  // totalCount * rowHeight so the scroll area is correctly sized and
  // fetchPage can trigger when the group scrolls into the viewport.
  const loadingPlaceholderHeight =
    groupLoading && virtualCount > 0 ? virtualCount * rowHeight : 0;

  const isEmpty =
    !groupLoading &&
    rows.length === 0 &&
    virtualCount === 0 &&
    !group.totalCount;
  const emptyContent = emptyState ?? <DataTableEmpty />;

  // -------------------------------------------------------------------------
  // Sticky group header: pinned both vertically and horizontally
  // -------------------------------------------------------------------------

  // Measured via ResizeObserver — the height feeds the column header's
  // sticky `top`.
  const [groupHeaderEl, setGroupHeaderEl] = React.useState<HTMLElement | null>(
    null,
  );

  React.useEffect(() => {
    if (!groupHeaderEl) return;
    const measure = () => {
      const nextHeight = groupHeaderEl.getBoundingClientRect().height;
      setGroupHeaderHeight((current) =>
        current === nextHeight ? current : nextHeight,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(groupHeaderEl);
    return () => observer.disconnect();
  }, [groupHeaderEl]);

  const stickyGroupHeaderStyle = React.useMemo<React.CSSProperties>(
    () => ({
      position: "sticky",
      top: 0,
      left: 0,
      width: containerWidth,
      zIndex: 30,
    }),
    [containerWidth],
  );

  // Registers this group's sticky header DOM node so the parent can scroll-to
  // it from the group navigator, and hands the same node to the height
  // measurement effect above.
  const headerNodeRefCb = React.useCallback(
    (node: HTMLDivElement | null) => {
      registerGroupHeaderNode(node);
      setGroupHeaderEl(node);
    },
    [registerGroupHeaderNode],
  );

  const renderGroupHeader = React.useCallback(() => {
    if (isUngrouped) return null;

    if (stickyEnabled) {
      return (
        <Box
          ref={headerNodeRefCb}
          // `group/gh` scopes the navigator's hover-reveal to this header only.
          className={cn(SURFACE_BG[surface], groupNavEnabled && "group/gh")}
          style={stickyGroupHeaderStyle}
        >
          {groupHeader}
        </Box>
      );
    }

    return groupHeader;
  }, [
    isUngrouped,
    stickyEnabled,
    headerNodeRefCb,
    groupNavEnabled,
    stickyGroupHeaderStyle,
    groupHeader,
    surface,
  ]);

  if (!isExpanded) {
    // Collapsed: render header + zero-height body placeholder to keep hooks consistent
    return (
      <>
        {renderGroupHeader()}
        <Box ref={groupBodyRef} style={{ height: 0, overflow: "hidden" }} />
      </>
    );
  }

  if (group.placeholder) {
    // Placeholder: render a fixed-height empty view sized to totalCount × rowHeight.
    // No virtualizer, no data fetching — just reserves scroll space.
    // When the placeholder scrolls into view, call fetchPage to activate it.
    const placeholderHeight =
      (typeof group.totalCount === "number" ? group.totalCount : 0) * rowHeight;
    return (
      <>
        {renderGroupHeader()}
        <PlaceholderBody
          ref={groupBodyRef}
          height={placeholderHeight}
          onVisible={
            group.fetchPage ? () => group.fetchPage!(0, PAGE_SIZE) : undefined
          }
        />
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Body: real table markup, display-overridden. The outer Box owns all
  // scrolling — the base Table's overflow container is collapsed via its
  // render prop, and header/body flow directly in the shared scroll context
  // (sticky headers need unobstructed access to the scroll ancestor).
  // Empty and loading states are NOT table content, so they render as
  // siblings of the <table>, never inside it.
  // -------------------------------------------------------------------------

  const showHeader = !hideHeader && (!isEmpty || !isUngrouped);
  const showRows = !isEmpty && loadingPlaceholderHeight === 0;

  const bodyContent = (
    <Box
      ref={groupBodyRef}
      className={cn(
        !isUngrouped ? "mb-4" : undefined,
        // The empty state stretches to fill the table, which needs an
        // unbroken `flex-1` chain — declared explicitly on this branch only.
        isEmpty && isUngrouped ? "flex flex-1 flex-col" : undefined,
      )}
    >
      <Box
        className={isEmpty && isUngrouped ? "flex flex-1 flex-col" : undefined}
        style={isEmpty ? undefined : TABLE_MIN_WIDTH_STYLE}
      >
        {showHeader || showRows ? (
          <Table
            variant="default"
            render={<Box overflow="visible" />}
            role="table"
            className="block"
            style={TABLE_MIN_WIDTH_STYLE}
          >
            {showHeader ? renderHeader() : null}
            {showRows ? (
              <TableBody
                ref={rowsContainerRef}
                className="relative block w-full"
                style={
                  virtualizer
                    ? { height: virtualizer.getTotalSize() }
                    : undefined
                }
              >
                {renderVirtualRows()}
              </TableBody>
            ) : null}
          </Table>
        ) : null}
        {isEmpty ? (
          emptyContent
        ) : loadingPlaceholderHeight > 0 ? (
          <Box style={{ height: loadingPlaceholderHeight }} />
        ) : null}
      </Box>
    </Box>
  );

  return (
    <DataTableShiftSelectProvider>
      <DataTableGroupRowsProvider rows={rows}>
        {renderGroupHeader()}
        {bodyContent}
      </DataTableGroupRowsProvider>
    </DataTableShiftSelectProvider>
  );
}

DataTableGroupContentInner.displayName = "DataTableGroupContent";

// Memoize to prevent re-rendering groups whose data hasn't changed.
// The `group` prop is always a new object (from the parent's useMemo), so we
// shallow-compare its individual fields instead of using default reference equality.
function groupPropsAreEqual<TData>(
  prev: DataTableGroupContentProps<TData>,
  next: DataTableGroupContentProps<TData>,
): boolean {
  if (prev.group !== next.group) {
    const pg = prev.group;
    const ng = next.group;
    if (
      pg.data !== ng.data ||
      pg.totalCount !== ng.totalCount ||
      pg.dataOffset !== ng.dataOffset ||
      pg.loading !== ng.loading ||
      pg.placeholder !== ng.placeholder ||
      pg.fetchPage !== ng.fetchPage ||
      pg.label !== ng.label ||
      pg.key !== ng.key
    )
      return false;
  }
  // Shallow-compare all other props
  const keys = Object.keys(next) as (keyof DataTableGroupContentProps<TData>)[];
  for (const key of keys) {
    if (key === "group") continue;
    if (prev[key] !== next[key]) return false;
  }
  return true;
}

const DataTableGroupContent = React.memo(
  DataTableGroupContentInner,
  groupPropsAreEqual,
) as typeof DataTableGroupContentInner;

// ---------------------------------------------------------------------------
// DataTable — main component
// ---------------------------------------------------------------------------

/**
 * DataTable — a grouped, virtualized data table component.
 *
 * Renders each group as an independent virtualized sub-table with its own
 * TanStack Table instance and virtualizer.  When `totalCount` exceeds loaded
 * `data.length`, the virtualizer sizes the scroll area to the total and
 * renders skeleton rows for unloaded indices.  Scrolling to an unloaded
 * region calls `fetchPage(offset, limit)` (debounced 300ms) so the provider
 * can re-fetch at that position.
 *
 * Ungrouped = one group. Grouped = N groups. Same code path.
 *
 * @example
 * ```tsx
 * // Ungrouped
 * <DataTable
 *   columns={columns}
 *   groups={[{ key: '__all__', label: 'All', data: members, totalCount: 200, fetchPage }]}
 * />
 *
 * // Grouped
 * <DataTable
 *   columns={columns}
 *   groups={[
 *     { key: 'team-a', label: 'Team A', data: teamAData, totalCount: 80, fetchPage: fetchA },
 *     { key: 'team-b', label: 'Team B', data: teamBData, totalCount: 45, fetchPage: fetchB },
 *   ]}
 *   groupTitle={(key) => <TeamLabel name={key} />}
 * />
 * ```
 */
export function DataTable<TData>({
  columns: columnsProp,
  groups,
  surface = "background",
  getRowId,
  sorting,
  onSortingChange,
  columnOrder: columnOrderProp,
  onColumnOrderChange,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  columnSizing: columnSizingProp,
  onColumnSizingChange,
  extraData,
  pinnedColumnIds = DEFAULT_PINNED_COLUMN_IDS,
  fillColumnId,
  stickyHeader = false,
  scrollMode = "internal",
  hideHeader = false,
  density = "regular",
  loading = false,
  emptyState,
  onRowClick,
  rowClickable,
  renderRowActions,
  "data-testid": dataTestId,
  groupTitle,
  groupActions,
  bulkActions,
  canSelectRow,
  hideGroupCount,
  paddingX,
  paddingXAlign = "edge",
  groupCounts,
  lazyGroups,
  onGroupActivate,
  groupNavigator = false,
}: DataTableProps<TData>) {
  const hasBulkActions = !!bulkActions?.length;
  const fitContent = scrollMode === "fit";
  const isMobile = useIsMobile();
  // Drag affordances (header reorder, resize handles) are pointer-shaped UI —
  // gate them on touch capability, not viewport width. A narrow desktop
  // window keeps them; an iPad in landscape (>768px) gets the settings
  // panel's up/down buttons instead of a dead drag.
  const isTouch = useIsTouchDevice();
  const effectivePinnedColumnIds = isMobile
    ? EMPTY_PINNED_COLUMN_IDS
    : pinnedColumnIds;

  // When bulkActions are provided, manage selection state per-group so that
  // one group's table operations never interfere with another group's
  // selections (important when pages are lazy-loaded).
  const [groupSelections, setGroupSelections] = React.useState<
    Record<string, RowSelectionState>
  >({});

  // Track which groups have "select all" active (meaning ALL items including
  // unloaded ones, not just visible rows).
  const [selectAllGroupKeys, setSelectAllGroupKeys] = React.useState<
    Set<string>
  >(new Set());

  // Stable per-group onRowSelectionChange handlers (cached in a ref so the
  // same function reference is returned for a given groupKey across renders).
  const groupSelectionHandlersRef = React.useRef(
    new Map<string, OnChangeFn<RowSelectionState>>(),
  );
  const getGroupSelectionHandler = React.useCallback(
    (groupKey: string): OnChangeFn<RowSelectionState> => {
      let handler = groupSelectionHandlersRef.current.get(groupKey);
      if (!handler) {
        handler = (updater) => {
          setGroupSelections((prev) => ({
            ...prev,
            [groupKey]:
              typeof updater === "function"
                ? updater(prev[groupKey] ?? {})
                : updater,
          }));
        };
        groupSelectionHandlersRef.current.set(groupKey, handler);
      }
      return handler;
    },
    [],
  );

  // Merged selection across all groups (for bulk actions bar + row data cache)
  const mergedRowSelection = React.useMemo(() => {
    const merged: RowSelectionState = {};
    for (const sel of Object.values(groupSelections)) {
      Object.assign(merged, sel);
    }
    return merged;
  }, [groupSelections]);

  // -------------------------------------------------------------------------
  // Lazy group activation + resolved groups
  // -------------------------------------------------------------------------

  const [activatedKeys, setActivatedKeys] = React.useState<Set<string>>(
    new Set(),
  );
  const onGroupActivateRef = React.useRef(onGroupActivate);
  onGroupActivateRef.current = onGroupActivate;

  const handleGroupActivate = React.useCallback((key: string) => {
    setActivatedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    onGroupActivateRef.current?.(key);
  }, []);

  const resolvedGroups = React.useMemo(() => {
    let resolved = groups;

    // Apply groupCounts to groups that don't already have a totalCount
    if (groupCounts) {
      resolved = resolved.map((g) => {
        const count = groupCounts[g.key];
        if (count !== undefined && g.totalCount === undefined) {
          return { ...g, totalCount: count };
        }
        return g;
      });
    }

    // Wire up lazy activation for placeholder groups
    if (lazyGroups) {
      resolved = resolved.map((g) => {
        if (!g.placeholder) return g;
        if (activatedKeys.has(g.key)) return g;
        return {
          ...g,
          fetchPage: () => handleGroupActivate(g.key),
        };
      });
    }

    return resolved;
  }, [groups, groupCounts, lazyGroups, activatedKeys, handleGroupActivate]);

  // Accumulate selected row data across page loads so the bulk actions bar
  // can resolve rows even after their page has scrolled out of view.
  const selectedRowDataRef = React.useRef(new Map<string, TData>());
  if (hasBulkActions && getRowId) {
    const sel = mergedRowSelection;
    const cache = selectedRowDataRef.current;
    // Remove deselected entries
    for (const id of cache.keys()) {
      if (!sel[id]) cache.delete(id);
    }
    // Capture data for any selected rows visible in current groups
    for (const group of resolvedGroups) {
      for (const [i, row] of group.data.entries()) {
        const id = getRowId(row, i);
        if (sel[id]) cache.set(id, row);
      }
    }
  }

  // Auto-prepend selection column and auto-append actions column
  const columns = React.useMemo(() => {
    let cols = columnsProp;
    if (hasBulkActions) {
      if (!cols.some((c) => c.id === "select")) {
        cols = [createSelectionColumn<TData>(), ...cols];
      }
    }
    if (renderRowActions) {
      cols = [...cols, createActionsColumn<TData>(renderRowActions)];
    }
    return cols;
  }, [columnsProp, hasBulkActions, renderRowActions]);

  const handleClearSelection = React.useCallback(() => {
    setGroupSelections({});
    setSelectAllGroupKeys(new Set());
  }, []);

  // Stable per-group setSelectAll callbacks (cached in a ref).
  const selectAllHandlersRef = React.useRef(
    new Map<string, (value: boolean) => void>(),
  );
  const getSelectAllHandler = React.useCallback((groupKey: string) => {
    let handler = selectAllHandlersRef.current.get(groupKey);
    if (!handler) {
      handler = (value: boolean) => {
        setSelectAllGroupKeys((prev) => {
          const next = new Set(prev);
          if (value) next.add(groupKey);
          else next.delete(groupKey);
          return next;
        });
      };
      selectAllHandlersRef.current.set(groupKey, handler);
    }
    return handler;
  }, []);

  // Derive selectAllGroups for the bulk actions bar.
  const selectAllGroups = React.useMemo<SelectAllGroup[]>(() => {
    if (selectAllGroupKeys.size === 0) return [];
    return resolvedGroups
      .filter((g) => selectAllGroupKeys.has(g.key))
      .map((g) => ({
        groupKey: g.key,
        totalCount:
          typeof g.totalCount === "number" ? g.totalCount : g.data.length,
      }));
  }, [resolvedGroups, selectAllGroupKeys]);

  const isUngrouped =
    resolvedGroups.length === 1 && resolvedGroups[0]?.key === ALL_GROUP_KEY;

  // -------------------------------------------------------------------------
  // Container width measurement
  // -------------------------------------------------------------------------

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [, notifyScrollMount] = React.useReducer((x: number) => x + 1, 0);
  const widthObserverRef = React.useRef<ResizeObserver | null>(null);
  const didNotifyScrollMountRef = React.useRef(false);
  const scrollRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;

      // Clean up previous observer
      if (widthObserverRef.current) {
        widthObserverRef.current.disconnect();
        widthObserverRef.current = null;
      }

      if (node) {
        // When the scroll container mounts, force all children (virtualizers)
        // to re-render so they can pick up the now-available scroll element.
        if (!didNotifyScrollMountRef.current) {
          didNotifyScrollMountRef.current = true;
          notifyScrollMount();
        }

        // Track container width via ResizeObserver. clientWidth excludes the
        // scrollbar, so the observer fires when a scrollbar appears/disappears
        // (content-box shrinks) — something a resize event alone misses.
        const observer = new ResizeObserver(() => {
          const w = node.clientWidth;
          if (w > 0) {
            setContainerWidth((current) => {
              if (current === w) return current;
              lastMeasuredTableContainerWidth = w;
              return w;
            });
          }
        });
        observer.observe(node);
        widthObserverRef.current = observer;
        return;
      }

      didNotifyScrollMountRef.current = false;
    },
    [notifyScrollMount],
  );
  // Clean up ResizeObserver on unmount
  React.useEffect(
    () => () => {
      widthObserverRef.current?.disconnect();
    },
    [],
  );

  const [containerWidth, setContainerWidth] = React.useState(() => {
    if (lastMeasuredTableContainerWidth > 0) {
      return lastMeasuredTableContainerWidth;
    }
    // First-mount estimate until the ResizeObserver reports. The table is
    // never wider than the viewport, so this over-estimates rather than
    // collapsing the columns for one frame.
    return document.documentElement.clientWidth;
  });

  // -------------------------------------------------------------------------
  // Column customization state (controlled props with internal fallbacks)
  // -------------------------------------------------------------------------
  //
  // Order and sizing have internal fallbacks because the table itself mutates
  // them (header drag, resize handles). Visibility is only ever changed by an
  // external settings surface, so it is read-only here.

  const [internalOrder, setInternalOrder] = React.useState<string[] | null>(
    null,
  );
  const [internalSizing, setInternalSizing] = React.useState<
    Record<string, number>
  >(EMPTY_COLUMN_SIZING);

  // Structural columns (`select`, `__actions__`) and pinned columns are never
  // hideable — the settings panel disables their switches, and this scrub
  // keeps a stale persisted payload (or a consumer mistake) from hiding them
  // anyway. TanStack honors `columnVisibility` state over a column's
  // `enableHiding: false`, so the entries must be dropped before reaching the
  // table. Uses the raw pinned prop (not the mobile-adjusted effective set)
  // so a phone and a desktop agree on what can be hidden.
  const visibility = React.useMemo<VisibilityState>(() => {
    const raw = columnVisibilityProp ?? EMPTY_COLUMN_VISIBILITY;
    let next: VisibilityState | null = null;
    for (const id of [...STRUCTURAL_COLUMN_IDS, ...pinnedColumnIds]) {
      if (id in raw) {
        next ??= { ...raw };
        delete next[id];
      }
    }
    return next ?? raw;
  }, [columnVisibilityProp, pinnedColumnIds]);
  const sizing = columnSizingProp ?? internalSizing;

  const handleOrderChange = React.useCallback(
    (next: string[]) => {
      if (onColumnOrderChange) onColumnOrderChange(next);
      else setInternalOrder(next);
    },
    [onColumnOrderChange],
  );

  const handleSizingChange = React.useCallback(
    (next: Record<string, number>) => {
      if (onColumnSizingChange) onColumnSizingChange(next);
      else setInternalSizing(next);
    },
    [onColumnSizingChange],
  );

  // Consumer column ids in definition order — the default display order.
  const defaultOrderIds = React.useMemo(
    () =>
      columnsProp
        .map((def) => getColumnDefId(def))
        .filter((id): id is string => id !== undefined),
    [columnsProp],
  );
  const userOrder = columnOrderProp ?? internalOrder ?? defaultOrderIds;

  // One merge rule for the whole feature: the settings panel renders from
  // mergeColumnOrder, so the grid must derive its order from the same merge —
  // otherwise a column added after the user persisted an order would appear
  // at different positions in the panel and the table.
  const mergedOrder = React.useMemo(
    () => mergeColumnOrder(userOrder, defaultOrderIds),
    [userOrder, defaultOrderIds],
  );

  // Structural columns bracket the consumer order. TanStack drops order ids
  // with no matching column, so this is safe when select/actions are absent.
  const tableColumnOrder = React.useMemo(
    () => ["select", ...mergedOrder, "__actions__"],
    [mergedOrder],
  );

  // Hidden-column roster for the actions column's "+" header, in table order.
  // Null (no visibility handler, or nothing hidden) renders no affordance.
  // Travels by context so the column DEFINITIONS stay referentially stable
  // across visibility edits — see DataTableAddColumnContext.
  const addColumn = React.useMemo(() => {
    if (!onColumnVisibilityChange) return null;
    const byId = new Map<string, DataTableColumnDef<TData>>();
    for (const def of columnsProp) {
      const id = getColumnDefId(def);
      if (id !== undefined) byId.set(id, def);
    }
    const entries = mergedOrder
      .filter((id) => byId.has(id) && visibility[id] === false)
      .map((id) => ({ id, label: getColumnLabel(byId.get(id)!, id) }));
    if (entries.length === 0) return null;
    return {
      entries,
      onAdd: (id: string) =>
        onColumnVisibilityChange({ ...visibility, [id]: true }),
    };
  }, [onColumnVisibilityChange, columnsProp, mergedOrder, visibility]);

  // `Required` so the two edges below are non-optional: both are always set
  // here, and every reader downstream indexes them without a guard.
  const columnPinning = React.useMemo<Required<ColumnPinningState>>(
    () => ({
      left: effectivePinnedColumnIds,
      right:
        isMobile || !renderRowActions
          ? EMPTY_PINNED_COLUMN_IDS
          : RIGHT_PINNED_ACTIONS,
    }),
    [effectivePinnedColumnIds, isMobile, renderRowActions],
  );

  // -------------------------------------------------------------------------
  // Resolved pixel widths — the single source of truth for layout
  // -------------------------------------------------------------------------

  // Visible column defs partitioned into the on-screen order (left pinned →
  // center → right pinned), mirroring how TanStack partitions the group
  // tables' headers/cells.
  const visualLayout = React.useMemo(() => {
    const byId = new Map<string, DataTableColumnDef<TData>>();
    for (const def of columns) {
      const id = getColumnDefId(def);
      if (id !== undefined && visibility[id] !== false) byId.set(id, def);
    }
    // tableColumnOrder is a full merge of persisted + default ids, so every
    // visible column is guaranteed a slot — no append fallback needed (one
    // would re-introduce a second ordering rule alongside mergeColumnOrder).
    const orderedIds: string[] = [];
    const seen = new Set<string>();
    for (const id of tableColumnOrder) {
      if (byId.has(id) && !seen.has(id)) {
        orderedIds.push(id);
        seen.add(id);
      }
    }
    const leftIds = columnPinning.left.filter((id) => byId.has(id));
    const rightIds = columnPinning.right.filter((id) => byId.has(id));
    const pinnedSet = new Set([...leftIds, ...rightIds]);
    const centerIds = orderedIds.filter((id) => !pinnedSet.has(id));
    const visualIds = [...leftIds, ...centerIds, ...rightIds];
    return {
      visualIds,
      leftIds,
      rightIds,
      reorderableIds: centerIds.filter(
        (id) => !STRUCTURAL_COLUMN_IDS.has(id),
      ),
      resolvable: visualIds.map((id) => ({ id, meta: byId.get(id)?.meta })),
    };
  }, [columns, visibility, tableColumnOrder, columnPinning]);

  // The fill column absorbs any width deficit so the table always reaches the
  // container's right edge (the actions column stays flush). The consumer's
  // fillColumnId stays elastic across resizes of OTHER columns; only once the
  // user explicitly sizes it (or none is designated/visible) does the
  // visually last consumer column take over. The stretch is display-only —
  // `baseWidth` is always the UNSTRETCHED width (it is also what the resize
  // bake persists), so a container-dependent stretched value never leaks into
  // stored sizing and the resize drag's startStretch math stays exact even
  // when the fill column is the visually last one.
  // `resolveColumnWidths` sets an entry for every column it is handed and
  // never deletes one, so the resulting map is keyed by exactly
  // `visualLayout.visualIds` — every lookup below it is a guaranteed hit.
  const { resolvedWidths, fillColumn } = React.useMemo(() => {
    const widths = resolveColumnWidths(
      visualLayout.resolvable,
      sizing,
      containerWidth,
      fillColumnId,
    );
    let fillId: string | null =
      fillColumnId !== undefined &&
      widths.has(fillColumnId) &&
      sizing[fillColumnId] === undefined
        ? fillColumnId
        : null;
    if (fillId === null) {
      for (let i = visualLayout.visualIds.length - 1; i >= 0; i--) {
        const id = visualLayout.visualIds[i];
        if (id === undefined || STRUCTURAL_COLUMN_IDS.has(id)) continue;
        fillId = id;
        break;
      }
    }
    if (fillId === null) {
      return { resolvedWidths: widths, fillColumn: null };
    }
    // Unstretched base. When resolveColumnWidths stretched fillColumnId
    // (pristine fill layout) the map already includes the stretch, so the
    // base is re-derived from the definition; in every other case the map
    // value IS the base (flex share or pixel width).
    const def = visualLayout.resolvable.find((c) => c.id === fillId);
    const baseWidth =
      fillId === fillColumnId && def !== undefined
        ? resolveColumnBaseWidth(def, sizing)
        : widths.get(fillId)!;
    let total = 0;
    for (const width of widths.values()) total += width;
    const totalAtBase = total - (widths.get(fillId)! - baseWidth);
    const deficit = Math.max(0, containerWidth - totalAtBase);
    widths.set(fillId, baseWidth + deficit);
    return { resolvedWidths: widths, fillColumn: { id: fillId, baseWidth } };
  }, [visualLayout, sizing, containerWidth, fillColumnId]);

  const totalResolvedWidth = React.useMemo(() => {
    let total = 0;
    for (const width of resolvedWidths.values()) total += width;
    return total;
  }, [resolvedWidths]);

  // Push widths + pinned offsets into CSS variables on the scroll container.
  // Cells reference the variables through stable style objects, so layout
  // changes repaint without re-rendering rows; an active resize drag writes
  // the same variables imperatively per frame and commits state on release.
  React.useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    for (const [id, width] of resolvedWidths) {
      el.style.setProperty(columnWidthVar(id), `${width}px`);
    }
    el.style.setProperty(TABLE_TOTAL_WIDTH_VAR, `${totalResolvedWidth}px`);
    let left = 0;
    for (const id of visualLayout.leftIds) {
      el.style.setProperty(columnLeftVar(id), `${left}px`);
      left += resolvedWidths.get(id)!;
    }
    let right = 0;
    for (const id of [...visualLayout.rightIds].reverse()) {
      el.style.setProperty(columnRightVar(id), `${right}px`);
      right += resolvedWidths.get(id)!;
    }
  }, [resolvedWidths, totalResolvedWidth, visualLayout]);

  // Per-column-id style objects (stable per layout) for header and body
  // cells: CSS-var width plus sticky pinned positioning. Pinned header cells
  // sit at the sticky-top ∩ sticky-left/right intersection → z-30; pinned
  // body cells → z-10.
  const { headerCellStyles, bodyCellStyles } = React.useMemo(() => {
    const header = new Map<string, React.CSSProperties>();
    const body = new Map<string, React.CSSProperties>();
    const leftSet = new Set(visualLayout.leftIds);
    const rightSet = new Set(visualLayout.rightIds);
    for (const id of visualLayout.visualIds) {
      const width = `var(${columnWidthVar(id)})`;
      const base: React.CSSProperties = {
        width,
        minWidth: width,
        maxWidth: width,
      };
      let headerStyle: React.CSSProperties = base;
      let bodyStyle: React.CSSProperties = base;
      if (leftSet.has(id)) {
        const left = `var(${columnLeftVar(id)})`;
        headerStyle = { ...base, position: "sticky", left, zIndex: 30 };
        bodyStyle = { ...base, position: "sticky", left, zIndex: 10 };
      } else if (rightSet.has(id)) {
        const right = `var(${columnRightVar(id)})`;
        headerStyle = { ...base, position: "sticky", right, zIndex: 30 };
        bodyStyle = { ...base, position: "sticky", right, zIndex: 10 };
      }
      header.set(id, headerStyle);
      body.set(id, bodyStyle);
    }
    return { headerCellStyles: header, bodyCellStyles: body };
  }, [visualLayout]);

  const reorderableIdSet = React.useMemo(
    () => new Set(visualLayout.reorderableIds),
    [visualLayout],
  );

  // -------------------------------------------------------------------------
  // Resize + reorder drag controllers
  // -------------------------------------------------------------------------

  const { controller: resizeController, activeResizeColumnId } =
    useColumnResize({
      scrollContainerRef,
      widths: resolvedWidths,
      pinnedLeftIds: visualLayout.leftIds,
      sizing,
      fillColumn,
      onCommit: handleSizingChange,
    });

  const handleColumnDrop = React.useCallback(
    (draggedId: string, beforeId: string | null) => {
      // Reorder within the MERGED order (not the raw persisted one) so the
      // committed value always carries every current column — the same shape
      // the settings panel commits.
      const without = mergedOrder.filter((id) => id !== draggedId);
      let insertAt: number;
      if (beforeId !== null) {
        const idx = without.indexOf(beforeId);
        insertAt = idx === -1 ? without.length : idx;
      } else {
        // Dropped past the last visible column — land after it so trailing
        // hidden ids keep their stored slots.
        const lastVisible =
          visualLayout.reorderableIds[visualLayout.reorderableIds.length - 1];
        const idx =
          lastVisible !== undefined ? without.indexOf(lastVisible) : -1;
        insertAt = idx === -1 ? without.length : idx + 1;
      }
      const next = [
        ...without.slice(0, insertAt),
        draggedId,
        ...without.slice(insertAt),
      ];
      if (next.every((id, i) => id === mergedOrder[i])) return;
      handleOrderChange(next);
    },
    [mergedOrder, visualLayout, handleOrderChange],
  );

  const { draggingColumnId, dropIndicatorX, didDragRef, setGhostEl } =
    useColumnReorder({
      scrollContainerRef,
      enabled: !isTouch && visualLayout.reorderableIds.length > 1,
      visualIds: visualLayout.visualIds,
      reorderableIds: visualLayout.reorderableIds,
      widths: resolvedWidths,
      onDrop: handleColumnDrop,
    });

  const draggingColumnLabel = React.useMemo(() => {
    if (draggingColumnId === null) return null;
    const def = columns.find((d) => getColumnDefId(d) === draggingColumnId);
    return def ? getColumnLabel(def, draggingColumnId) : draggingColumnId;
  }, [draggingColumnId, columns]);

  // Content-coordinate span of the dragged column — drives the dimming veil
  // over its body cells (an overlay instead of per-cell opacity so no row
  // re-renders on drag start).
  const draggingColumnRect = React.useMemo(() => {
    if (draggingColumnId === null) return null;
    let left = 0;
    for (const id of visualLayout.visualIds) {
      const width = resolvedWidths.get(id)!;
      if (id === draggingColumnId) return { left, width };
      left += width;
    }
    return null;
  }, [draggingColumnId, visualLayout.visualIds, resolvedWidths]);

  const ghostRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => setGhostEl(node),
    [setGhostEl],
  );

  // -------------------------------------------------------------------------
  // Group open/close state
  // -------------------------------------------------------------------------

  const [openGroupKeys, setOpenGroupKeys] = React.useState<Set<string>>(
    () => new Set(groups.map((g) => g.key)),
  );

  // When groups change (e.g. groupBy field changed), reset to default state
  const prevGroupKeysRef = React.useRef<string>("");
  const currentGroupKeysStr = groups.map((g) => g.key).join("\0");
  if (prevGroupKeysRef.current !== currentGroupKeysStr) {
    prevGroupKeysRef.current = currentGroupKeysStr;
    const currentKeys = new Set(groups.map((g) => g.key));
    // Scroll to top when the group set actually changes (field change, sort direction)
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
    setOpenGroupKeys(currentKeys);
    if (lazyGroups) setActivatedKeys(new Set());
  }

  const collapseAll = React.useCallback(() => {
    setOpenGroupKeys(new Set());
  }, []);

  const expandAll = React.useCallback(() => {
    setOpenGroupKeys(new Set(resolvedGroups.map((g) => g.key)));
  }, [resolvedGroups]);

  const handleGroupToggle = React.useCallback((key: string) => {
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Group navigator: sticky-header up/down nav + jump-to-group list.
  const groupNavEnabled =
    groupNavigator && stickyHeader && !isUngrouped && resolvedGroups.length > 1;

  // Plain-text labels only — the navigator list rows are themselves clickable,
  // so a rich (interactive) `groupTitle` chip would fight the row click.
  const navItems = React.useMemo<DataTableGroupNavItem[]>(
    () =>
      resolvedGroups.map((g) => ({
        key: g.key,
        label: g.label,
        count: hideGroupCount ? undefined : (g.totalCount ?? g.data.length),
      })),
    [resolvedGroups, hideGroupCount],
  );

  // Registry of sticky group-header DOM nodes keyed by group key, plus per-key
  // ref callbacks cached so a group keeps the same callback identity and doesn't
  // churn the registry.
  const groupHeaderNodesRef = React.useRef(new Map<string, HTMLElement>());
  const registerGroupHeaderHandlersRef = React.useRef(
    new Map<string, (node: HTMLElement | null) => void>(),
  );
  const getRegisterGroupHeader = React.useCallback((key: string) => {
    let handler = registerGroupHeaderHandlersRef.current.get(key);
    if (!handler) {
      handler = (node: HTMLElement | null) => {
        if (node) groupHeaderNodesRef.current.set(key, node);
        else groupHeaderNodesRef.current.delete(key);
      };
      registerGroupHeaderHandlersRef.current.set(key, handler);
    }
    return handler;
  }, []);

  const navigateToGroup = React.useCallback(
    (target: DataTableGroupNavTarget) => {
      const scrollEl = scrollContainerRef.current;
      if (!scrollEl) return;
      const scrollToTop = (top: number) => {
        scrollEl.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      };
      if (target.type === "top") {
        scrollToTop(0);
        return;
      }
      const key =
        target.type === "bottom"
          ? resolvedGroups[resolvedGroups.length - 1]?.key
          : target.key;
      const node = key ? groupHeaderNodesRef.current.get(key) : undefined;
      if (!node) {
        if (target.type === "bottom") scrollToTop(scrollEl.scrollHeight);
        return;
      }
      // Land the target header's CURRENT top at the container top; the header's
      // own `position: sticky` then pins it there.
      const delta =
        node.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;
      scrollToTop(scrollEl.scrollTop + delta);
    },
    [resolvedGroups],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // The scroll container doubles as the table's single `ContextMenuTrigger`
  // (see `DataTableOverlayProvider`), so it is built here and handed over
  // rather than nested inline.
  //
  // It is `relative` so the veil/indicator/ghost overlays position in content
  // coordinates and scroll with the rows, and takes `fill="flex"` (flex-1 PLUS
  // min-h-0/min-w-0), never a bare `flex-1`. A flex item's default
  // `min-height: auto` resolves to its content height, so a bare `flex-1`
  // refuses to shrink into the space the parent offers and stretches to the
  // full table height instead — `overflow-auto` then never has anything to
  // clip, the virtualizer measures a viewport as tall as the whole dataset,
  // and virtualization silently becomes a no-op that renders EVERY row.
  // `min-h-0` is what keeps the scroll box bounded.
  const scrollContainer = (
      <Box position="relative" direction="column"
        ref={scrollRefCallback}
        data-testid={dataTestId}
        fill={fitContent ? undefined : "flex"}
        className={cn(
          SURFACE_BG[surface],
          SURFACE_FILL_VAR[surface],
          scrollMode === "internal"
            ? // During a resize drag, force overflow-auto so a column
              // growing past the container isn't clipped mid-gesture.
              totalResolvedWidth > containerWidth ||
              activeResizeColumnId !== null
              ? "overflow-auto"
              : "overflow-y-auto overflow-x-hidden"
            : "overflow-visible",
          "[overscroll-behavior-x:none]",
          "[scrollbar-width:thin]",
          "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5",
          "[&::-webkit-scrollbar-track]:bg-transparent",
          "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30",
          "[&::-webkit-scrollbar-corner]:bg-transparent",
        )}
      >
          <DataTableHeaderDragProvider
            didDragRef={didDragRef}
            reorderableIds={reorderableIdSet}
          >
            {resolvedGroups.map((group, groupIndex) => (
              <DataTableSelectAllProvider
                key={group.key}
                isSelectAll={selectAllGroupKeys.has(group.key)}
                setSelectAll={getSelectAllHandler(group.key)}
              >
                <DataTableGroupContent
                  group={group}
                  groupKey={group.key}
                  columns={columns}
                  isExpanded={openGroupKeys.has(group.key)}
                  density={density}
                  getRowId={getRowId}
                  sorting={sorting}
                  onSortingChange={onSortingChange}
                  rowSelection={
                    hasBulkActions
                      ? (groupSelections[group.key] ?? EMPTY_ROW_SELECTION)
                      : undefined
                  }
                  onRowSelectionChange={
                    hasBulkActions
                      ? getGroupSelectionHandler(group.key)
                      : undefined
                  }
                  canSelectRow={canSelectRow}
                  columnPinning={columnPinning}
                  tableColumnOrder={tableColumnOrder}
                  tableColumnVisibility={visibility}
                  headerCellStyles={headerCellStyles}
                  bodyCellStyles={bodyCellStyles}
                  resizeController={resizeController}
                  activeResizeColumnId={activeResizeColumnId}
                  draggingColumnId={draggingColumnId}
                  canResizeColumns={!isTouch}
                  reorderableIdSet={reorderableIdSet}
                  reorderableIds={visualLayout.reorderableIds}
                  onColumnMove={handleColumnDrop}
                  containerWidth={containerWidth}
                  loading={loading}
                  emptyState={emptyState}
                  onRowClick={onRowClick}
                  rowClickable={rowClickable}
                  extraData={extraData}
                  scrollContainerRef={scrollContainerRef}
                  groupTitle={groupTitle}
                  groupActions={groupActions}
                  onToggle={handleGroupToggle}
                  collapseAll={collapseAll}
                  expandAll={expandAll}
                  allCollapsed={openGroupKeys.size === 0}
                  stickyHeader={stickyHeader}
                  scrollMode={scrollMode}
                  hideHeader={hideHeader}
                  isUngrouped={isUngrouped}
                  hideGroupCount={hideGroupCount}
                  paddingX={paddingX}
                  paddingXAlign={paddingXAlign}
                  renderRowActions={renderRowActions}
                  groupNavEnabled={groupNavEnabled}
                  groupIndex={groupIndex}
                  groupTotal={resolvedGroups.length}
                  navItems={navItems}
                  navigateToGroup={navigateToGroup}
                  registerGroupHeaderNode={getRegisterGroupHeader(group.key)}
                />
              </DataTableSelectAllProvider>
            ))}
          </DataTableHeaderDragProvider>
          {draggingColumnRect !== null ? (
            // Dimming veil over the dragged column — reads as "lifted".
            <Box pointerEvents="none" position="absolute" zIndex={15}
              className={cn("top-0 bottom-0", SURFACE_VEIL[surface],
              )}
              style={{
                left: draggingColumnRect.left,
                width: draggingColumnRect.width,
              }}
            />
          ) : null}
          {dropIndicatorX !== null ? (
            <Box pointerEvents="none" position="absolute" zIndex={50} rounded="full" duration={150}
              className="top-0 bottom-0 w-0.5 bg-primary transition-[left] ease-out"
              style={{ left: dropIndicatorX - 1 }}
            >
              <Box position="absolute" rounded="full" className="top-0 -left-0.5 size-1.5 bg-primary" />
            </Box>
          ) : null}
          {draggingColumnId !== null ? (
            // Floating copy of the dragged column's header, positioned
            // imperatively by useColumnReorder's rAF loop.
            <Box pointerEvents="none" position="absolute" zIndex={60}
              ref={ghostRefCallback}
              surface="popover"
              rounded="md"
              border
              paddingX="sm"
              paddingY="2xs"
              className="top-0 left-0 will-change-transform"
            >
              <Text size="sm" weight="medium" truncate>
                {draggingColumnLabel}
              </Text>
            </Box>
          ) : null}
      </Box>
  );

  return (
    // Root is a flex column: the `flex-1` stretch chain and the empty state's
    // fill depend on it. Same `fill="flex"` rule as the scroll container above.
    <Box position="relative" direction="column"
      fill={fitContent ? undefined : "flex"}
    >
      {/* Surface and density both wrap the overlay host, not just the scroll
          container: the popups render from the host, OUTSIDE the scrolling
          subtree, and still have to resolve the same surface and the same density
          as the cells they came from. */}
      <DataTableAddColumnProvider value={addColumn}>
      <DataTableSurfaceProvider surface={surface}>
        <DataTableDensityProvider density={density}>
          <DataTableOverlayProvider scrollContainer={scrollContainer} />
        </DataTableDensityProvider>

        {hasBulkActions && getRowId && (
          <BulkActionsBar
            actions={bulkActions}
            // The bar takes plain ids; the table's TanStack selection map
            // reduces to its truthy keys.
            selectedIds={
              new Set(
                Object.keys(mergedRowSelection).filter(
                  (id) => mergedRowSelection[id],
                ),
              )
            }
            onClearSelection={handleClearSelection}
            selectedRowData={selectedRowDataRef.current}
            selectAllGroups={selectAllGroups}
          />
        )}
      </DataTableSurfaceProvider>
      </DataTableAddColumnProvider>
    </Box>
  );
}

DataTable.displayName = "DataTable";

export { DataTableCell };
